import React, { useState } from 'react';
import { Card, Tabs, Table, Input, Button, Tag, Typography, Space, Popconfirm, Segmented, Switch, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import AutoTradePanel from './AutoTradePanel';
import AlertPanel from './AlertPanel';
import { useI18n } from '../i18n';

const ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;

// 地址簿管理：自定义地址名称，用于全站地址显示增强
const AddressBookPanel = ({ entries, onChanged }) => {
  const { t, ts } = useI18n();
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  // internal=内部（成交统计与 10 个固定钱包归为一组），external=外部
  const [identity, setIdentity] = useState('external');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!ADDRESS_PATTERN.test(address.trim())) {
      message.warning(t('请输入合法的 0x 开头 42 位地址'));
      return;
    }
    if (!name.trim()) {
      message.warning(t('请输入名称'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/address-book', {
        address: address.trim(),
        name: name.trim(),
        isInternal: identity === 'internal'
      });
      message.success(t('已保存（同地址重复保存会更新名称与身份）'));
      setAddress('');
      setName('');
      setIdentity('external');
      onChanged();
    } catch (err) {
      message.error(t('保存失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 列表内直接切换内部/外部（按地址幂等覆盖保存）
  const handleToggleInternal = async (entry, checked) => {
    try {
      await api.post('/address-book', {
        address: entry.address,
        name: entry.name,
        isInternal: checked
      });
      message.success(t('「{name}」已标记为{type}', { name: entry.name, type: checked ? t('内部') : t('外部') }));
      onChanged();
    } catch (err) {
      message.error(t('更新失败: ') + ts(err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await api.delete(`/address-book/${entryId}`);
      message.success(t('已删除'));
      onChanged();
    } catch (err) {
      message.error(t('删除失败: ') + ts(err.response?.data?.error || err.message));
    }
  };

  const columns = [
    {
      title: t('名称'),
      dataIndex: 'name',
      width: 200,
      render: (v) => <Tag color="gold">{v}</Tag>
    },
    {
      title: t('地址'),
      dataIndex: 'address',
      render: (v) => (
        <Typography.Text style={{ fontFamily: 'monospace' }} copyable={{ text: v }}>
          {v}
        </Typography.Text>
      )
    },
    {
      title: t('身份'),
      dataIndex: 'isInternal',
      width: 140,
      render: (v, e) => (
        <Space size={8}>
          {v ? <Tag color="blue">{t('内部')}</Tag> : <Tag>{t('外部')}</Tag>}
          <Switch
            size="small"
            checked={Boolean(v)}
            checkedChildren={t('内')}
            unCheckedChildren={t('外')}
            onChange={(checked) => handleToggleInternal(e, checked)}
          />
        </Space>
      )
    },
    {
      title: t('操作'),
      key: 'actions',
      width: 100,
      render: (_, e) => (
        <Popconfirm title={t('删除「{name}」？', { name: e.name })} okText={t('删除')} cancelText={t('取消')} onConfirm={() => handleDelete(e.entryId)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            {t('删除')}
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        {t('为常用地址设置名称后，交易记录等处会以名称标签展示该地址；标记为「内部」的地址在 PancakeSwap 成交统计中与 10 个固定钱包一起归入内部组')}
      </Typography.Text>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          style={{ flex: 2, fontFamily: 'monospace' }}
          placeholder={t('0x 开头的 42 位地址')}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          allowClear
        />
        <Input
          style={{ flex: 1 }}
          placeholder={t('名称（如：币安提币、老板钱包）')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          allowClear
          onPressEnter={handleSave}
        />
        <Segmented
          value={identity}
          onChange={setIdentity}
          options={[
            { label: t('外部'), value: 'external' },
            { label: t('内部'), value: 'internal' }
          ]}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleSave} loading={saving}>
          {t('保存')}
        </Button>
      </Space.Compact>
      <Table
        rowKey="entryId"
        columns={columns}
        dataSource={entries}
        size="middle"
        pagination={{ pageSize: 10, showSizeChanger: false }}
      />
    </Space>
  );
};

// 设置页：内部分 tab，当前含地址簿（后续设置项在 items 中扩展）
const Settings = ({ addressBook, onAddressBookChanged }) => {
  const { t } = useI18n();
  return (
    <Card title={t('设置')}>
      <Tabs
        defaultActiveKey="address-book"
        items={[
          {
            key: 'address-book',
            label: t('地址簿'),
            children: <AddressBookPanel entries={addressBook} onChanged={onAddressBookChanged} />
          },
          {
            key: 'auto-trade',
            label: t('自动交易'),
            children: <AutoTradePanel />
          },
          {
            key: 'price-alert',
            label: t('价格提醒'),
            children: <AlertPanel />
          }
        ]}
      />
    </Card>
  );
};

export default Settings;
